import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader, { DarkPage } from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Layers, AlertCircle, TrendingUp, Wallet, Activity, ArrowRight, Trash2, ArrowDown, ArrowUp, Minus, ChevronsDown } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";

const HEALTH_COLORS = { green: "#047857", amber: "#F59E0B", red: "#FF007A" };
const HEALTH_DOT = { green: "bg-emerald-700", amber: "bg-[#F59E0B]", red: "bg-[#FF007A]" };

// ─── Margin bar (0-50% scale) ────────────────────────────────────────────────
const MarginBar = ({ projected, target }) => {
  const scale = 50;
  const projPct = Math.min(100, Math.max(0, (projected / scale) * 100));
  const targetPct = Math.min(100, Math.max(0, (target / scale) * 100));
  const isOnTrack = projected >= target - 5;
  const isCritical = projected < target - 15;
  const barColor = isCritical ? "#FF007A" : isOnTrack ? "#00C896" : "#F59E0B";
  const hatchWidth = Math.max(0, targetPct - projPct);
  return (
    <div className="w-full min-w-[120px]">
      <div className="h-5 bg-[#1F2937] w-full relative overflow-hidden">
        <div className="absolute left-0 top-0 h-full" style={{ width: `${projPct}%`, backgroundColor: barColor }} />
        {hatchWidth > 0 && (
          <div
            className="absolute top-0 h-full"
            style={{
              left: `${projPct}%`,
              width: `${hatchWidth}%`,
              backgroundImage: `repeating-linear-gradient(45deg, ${barColor}60 0, ${barColor}60 3px, transparent 0, transparent 50%)`,
              backgroundSize: "8px 8px",
            }}
          />
        )}
        <div className="absolute top-0 h-full w-px bg-white/50" style={{ left: `${targetPct}%` }} />
      </div>
      <div className="flex justify-between text-[9px] font-mono text-[#4B5563] mt-0.5">
        <span style={{ color: barColor }} className="font-semibold">{projected.toFixed(1)}%</span>
        <span className="text-[#6B7280]">tgt {target}%</span>
      </div>
    </div>
  );
};

// ─── Margin status helpers ───────────────────────────────────────────────────
const getMarginStatus = (margin, health) => {
  if (!margin || !margin.configured) {
    if (health === "red") return "Critical";
    if (health === "amber") return "Warning";
    return "On track";
  }
  const diff = margin.current_margin_percentage - margin.target_margin_percentage;
  if (diff >= -5) return "On track";
  if (diff >= -15) return "Warning";
  return "Critical";
};

const MARGIN_STATUS_STYLE = {
  "On track": "bg-emerald-100 text-emerald-800 border-emerald-200",
  Warning:    "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30",
  Critical:   "bg-[#FF007A]/10 text-[#FF007A] border-[#FF007A]/30",
};

const FILTER_TABS = ["All", "On track", "Warning", "Critical"];

const TrendArrow = ({ status }) => {
  if (status === "Critical") return <ArrowDown className="w-3.5 h-3.5 text-[#FF007A]" />;
  if (status === "Warning")  return <ArrowDown className="w-3.5 h-3.5 text-[#F59E0B]" />;
  return <Minus className="w-3.5 h-3.5 text-[#374151]" />;
};

// ─── Live Projects — Margin Monitor ─────────────────────────────────────────
const MarginMonitor = ({ projects, marginMap }) => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("All");
  const [sortErosion, setSortErosion] = useState(false);

  const liveCount = projects.length;
  const contractValueLive = projects.reduce((s, p) => s + (marginMap[p.project_id]?.contract_value || p.contract_value || p.budget_cash || 0), 0);
  const projectedMargins = projects.map((p) => {
    const m = marginMap[p.project_id];
    return m?.configured ? m.current_margin_percentage : null;
  }).filter((v) => v !== null);
  const avgProjectedMargin = projectedMargins.length
    ? projectedMargins.reduce((s, v) => s + v, 0) / projectedMargins.length
    : null;
  const atRiskCount = projects.filter((p) => {
    const status = getMarginStatus(marginMap[p.project_id], p.health);
    return status !== "On track";
  }).length;

  const rows = projects
    .map((p) => {
      const margin = marginMap[p.project_id];
      const status = getMarginStatus(margin, p.health);
      const projected = margin?.configured ? margin.current_margin_percentage : null;
      const target = margin?.configured ? margin.target_margin_percentage : null;
      const erosion = projected !== null && target !== null ? target - projected : null;
      return { ...p, margin, status, projected, target, erosion };
    })
    .filter((r) => filter === "All" || r.status === filter)
    .sort((a, b) => {
      if (!sortErosion) return 0;
      const ea = a.erosion ?? -Infinity;
      const eb = b.erosion ?? -Infinity;
      return eb - ea; // descending (worst first)
    });

  return (
    <div className="border border-[#1F2937]">
      {/* Section header */}
      <div className="border-b border-[#1F2937] px-6 py-3 bg-[#111827] flex items-center gap-3">
        <Activity className="w-4 h-4 text-[#00F5D4]" />
        <div className="font-display font-bold tracking-tight text-sm text-white">Live projects — Margin Monitor</div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] ml-auto">{liveCount} project{liveCount !== 1 ? "s" : ""}</div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#1F2937]">
        {[
          { label: "Live projects",         value: liveCount,                                                        color: "text-white" },
          { label: "Contract value live",   value: `£${contractValueLive.toLocaleString()}`,                        color: "text-[#00F5D4]" },
          { label: "Projected book margin", value: avgProjectedMargin !== null ? `${avgProjectedMargin.toFixed(1)}%` : "—", color: "text-[#00F5D4]" },
          { label: "Projects at risk",      value: atRiskCount,                                                     color: atRiskCount > 0 ? "text-[#FF007A]" : "text-white" },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-[#111827] px-6 py-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">{label}</div>
            <div className={`font-display font-black text-2xl tracking-tighter mt-1 ${color}`}>{value}</div>
          </div>
        ))}
      </div>

      {/* Filters + sort */}
      <div className="px-5 py-3 border-b border-[#1F2937] bg-[#0a0f1a] flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setFilter(tab)}
              className={`px-3 py-1 text-[10px] uppercase tracking-[0.15em] border transition font-mono ${
                filter === tab
                  ? "border-[#00F5D4]/50 bg-[#00F5D4]/10 text-[#00F5D4]"
                  : "border-[#1F2937] text-[#6B7280] hover:border-[#374151] hover:text-white"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
        <button
          onClick={() => setSortErosion((v) => !v)}
          className={`ml-auto flex items-center gap-1.5 px-3 py-1 text-[10px] uppercase tracking-[0.15em] border font-mono transition ${
            sortErosion
              ? "border-[#F59E0B]/50 bg-[#F59E0B]/10 text-[#F59E0B]"
              : "border-[#1F2937] text-[#6B7280] hover:border-[#374151] hover:text-white"
          }`}
        >
          <ChevronsDown className="w-3 h-3" />
          Sort: Erosion
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-[#030712] text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">
            <tr>
              <th className="text-left px-4 py-2">Project</th>
              <th className="text-right px-4 py-2">Contract value</th>
              <th className="text-right px-4 py-2">Complete</th>
              <th className="px-4 py-2">Projected margin vs target</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-center px-4 py-2">Trend</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan="6" className="px-4 py-6 text-center text-[#6B7280] text-xs">
                  No projects match this filter.
                </td>
              </tr>
            )}
            {rows.map((p) => {
              const contractVal = p.margin?.contract_value || p.contract_value || p.budget_cash || 0;
              const completePct = p.completion_pct || 0;
              return (
                <tr
                  key={p.project_id}
                  className="border-t border-[#1F2937] hover:bg-[#111827] cursor-pointer transition-colors"
                  onClick={() => navigate(`/burndown?project_id=${p.project_id}`)}
                  data-testid={`margin-row-${p.project_id}`}
                >
                  {/* Project name + client */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${HEALTH_DOT[p.health] || "bg-[#374151]"}`} />
                      <div>
                        <div className="font-medium text-white">{p.name}</div>
                        {p.description && (
                          <div className="text-[11px] text-[#6B7280] mt-0.5 truncate max-w-[220px]">{p.description}</div>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Contract value */}
                  <td className="px-4 py-3 text-right font-mono text-[#9CA3AF]">
                    {contractVal > 0 ? `£${contractVal.toLocaleString()}` : <span className="text-[#374151]">—</span>}
                  </td>

                  {/* Complete — mini progress bar */}
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-col items-end gap-1">
                      <span className="font-mono text-[#9CA3AF] text-xs">{completePct}%</span>
                      <div className="h-1.5 w-20 bg-[#1F2937]">
                        <div className="h-full bg-[#00F5D4]" style={{ width: `${completePct}%` }} />
                      </div>
                    </div>
                  </td>

                  {/* Projected margin vs target bar */}
                  <td className="px-4 py-3 w-48">
                    {p.projected !== null && p.target !== null ? (
                      <MarginBar projected={p.projected} target={p.target} />
                    ) : (
                      <span className="text-[#374151] text-sm">—</span>
                    )}
                  </td>

                  {/* Status badge */}
                  <td className="px-4 py-3">
                    <span className={`inline-flex text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border ${MARGIN_STATUS_STYLE[p.status]}`}>
                      {p.status}
                    </span>
                  </td>

                  {/* Trend arrow */}
                  <td className="px-4 py-3 text-center">
                    <div className="flex justify-center">
                      <TrendArrow status={p.status} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Main Portfolio component ────────────────────────────────────────────────
const Portfolio = () => {
  const [overview, setOverview] = useState(null);
  const [groups, setGroups] = useState([]);
  const [projects, setProjects] = useState([]);
  const [pulseDash, setPulseDash] = useState(null);
  const [marginMap, setMarginMap] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", project_ids: [], color: "#00F5D4" });

  const load = async () => {
    const [o, g, p, d] = await Promise.all([
      api.get("/portfolio/overview"),
      api.get("/portfolio/groups"),
      api.get("/projects"),
      api.get("/dashboard/portfolio"),
    ]);
    setOverview(o.data);
    setGroups(g.data);
    setProjects(p.data);
    setPulseDash(d.data);

    // Fetch margin status for each project in parallel
    const marginResults = await Promise.allSettled(
      p.data.map((proj) => api.get(`/projects/${proj.project_id}/margin-status`))
    );
    const map = {};
    p.data.forEach((proj, i) => {
      if (marginResults[i].status === "fulfilled") {
        map[proj.project_id] = marginResults[i].value.data;
      }
    });
    setMarginMap(map);
  };

  useEffect(() => { load(); }, []);

  const createGroup = async () => {
    if (!form.name.trim()) return;
    try {
      await api.post("/portfolio/groups", form);
      setOpen(false);
      setForm({ name: "", description: "", project_ids: [], color: "#00F5D4" });
      toast.success("Portfolio created");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create"); }
  };

  const deleteGroup = async (gid) => {
    if (!window.confirm("Delete this portfolio? Projects are not affected.")) return;
    await api.delete(`/portfolio/groups/${gid}`);
    toast.success("Portfolio removed");
    load();
  };

  const toggleProject = (pid) =>
    setForm((f) => ({
      ...f,
      project_ids: f.project_ids.includes(pid)
        ? f.project_ids.filter((x) => x !== pid)
        : [...f.project_ids, pid],
    }));

  if (!overview) return (
    <div className="min-h-screen bg-[#030712] flex items-center justify-center">
      <div className="text-xs uppercase tracking-[0.25em] text-[#6B7280]">Loading portfolio…</div>
    </div>
  );

  const healthData = Object.entries(overview.health).map(([k, v]) => ({ name: k, value: v }));
  const statusData = Object.entries(overview.status_distribution).map(([k, v]) => ({ name: k.replace("_", " "), value: v }));

  return (
    <DarkPage>
      <PageHeader
        eyebrow="Portfolio Management"
        title="Portfolio command centre"
        description="Cross-project rollup — health, completion, billing, and risks at a glance."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-[#00F5D4] text-[#030712] hover:bg-[#00e5c4] font-bold shadow-glow-teal" data-testid="new-portfolio-btn">
                <Plus className="w-4 h-4 mr-1" /> New portfolio
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-[#1F2937] bg-[#111827] text-white sm:max-w-xl">
              <DialogHeader><DialogTitle className="font-display tracking-tight text-white">New portfolio group</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Name</Label>
                  <Input className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white" data-testid="portfolio-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Description</Label>
                  <Textarea className="rounded-none mt-1 bg-[#030712] border-[#374151] text-white" data-testid="portfolio-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">Projects in portfolio</Label>
                  <div className="border border-[#374151] mt-1 max-h-60 overflow-y-auto bg-[#030712]">
                    {projects.length === 0 && <div className="text-xs text-[#6B7280] p-3">No projects yet — create some first.</div>}
                    {projects.map((p) => (
                      <label key={p.project_id} className="flex items-center gap-2 px-3 py-2 border-b border-[#1F2937] hover:bg-[#1F2937] cursor-pointer">
                        <Checkbox checked={form.project_ids.includes(p.project_id)} onCheckedChange={() => toggleProject(p.project_id)} data-testid={`portfolio-proj-${p.project_id}`} />
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${HEALTH_DOT[p.health] || "bg-[#374151]"}`} />
                        <span className="text-sm text-white">{p.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={createGroup} className="rounded-none bg-[#00F5D4] text-[#030712] hover:bg-[#00e5c4] font-bold" data-testid="confirm-create-portfolio">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="px-5 sm:px-8 lg:px-12 py-8 space-y-8">
        {/* Top KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-[#1F2937] border border-[#1F2937]">
          {[
            { label: "Projects",      testid: "kpi-projects",       value: overview.total_projects,                                    icon: Layers,     color: "text-white" },
            { label: "Avg completion",testid: "kpi-avg-completion", value: `${overview.avg_completion}%`,                             icon: Activity,   color: "text-[#00F5D4]" },
            { label: "Open RAID",     testid: "kpi-open-raid",      value: overview.open_raid,                                        icon: AlertCircle,color: "text-[#FF007A]" },
            { label: "Billed",        testid: "kpi-billed",         value: `£${(overview.total_billed || 0).toLocaleString()}`,       icon: TrendingUp, color: "text-[#00F5D4]" },
            { label: "Outstanding",   testid: "kpi-outstanding",    value: `£${(overview.total_outstanding || 0).toLocaleString()}`,  icon: Wallet,     color: "text-[#F59E0B]" },
          ].map(({ label, testid, value, icon: Icon, color }) => (
            <div key={label} className="bg-[#111827] p-6">
              <div className="flex items-center justify-between">
                <Icon className="w-4 h-4 text-[#374151]" />
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">{label}</div>
              </div>
              <div className={`font-display font-black text-3xl tracking-tighter mt-2 ${color}`} data-testid={testid}>{value}</div>
            </div>
          ))}
        </div>

        <PulseHealthPanel pulseDash={pulseDash} />

        {/* ── LIVE PROJECTS — MARGIN MONITOR ── */}
        <MarginMonitor projects={projects} marginMap={marginMap} />

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#1F2937] border border-[#1F2937]">
          <div className="bg-[#111827] p-6">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#6B7280] mb-4">Health distribution</div>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={healthData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} stroke="#FFFFFF" strokeWidth={2}>
                  {healthData.map((e) => <Cell key={e.name} fill={HEALTH_COLORS[e.name] || "#374151"} />)}
                </Pie>
                <Legend wrapperStyle={{ color: "#9CA3AF", fontSize: 11 }} />
                <Tooltip contentStyle={{ backgroundColor: "#FFFFFF", border: "1px solid #E5E7EB", color: "#111827" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-[#111827] p-6">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#6B7280] mb-4">Status distribution</div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={statusData}>
                <CartesianGrid stroke="#1F2937" />
                <XAxis dataKey="name" stroke="#6B7280" fontSize={11} />
                <YAxis stroke="#6B7280" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{ backgroundColor: "#FFFFFF", border: "1px solid #E5E7EB", color: "#111827" }} />
                <Bar dataKey="value" fill="#4F46E5" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Attention list */}
        {overview.attention?.length > 0 && (
          <div className="border border-[#1F2937] bg-[#111827]">
            <div className="border-b border-[#1F2937] px-6 py-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-[#FF007A]" />
              <div className="font-display font-bold tracking-tight text-sm text-white">Needs attention</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] ml-auto">{overview.attention.length} project(s)</div>
            </div>
            <div className="divide-y divide-[#1F2937]">
              {overview.attention.map((p) => (
                <Link key={p.project_id} to={`/workspace/${p.project_id}`} className="flex items-center gap-3 px-6 py-3 hover:bg-[#1a2535]" data-testid={`attention-${p.project_id}`}>
                  <span className={`inline-block w-2 h-2 rounded-full ${HEALTH_DOT[p.health]}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate text-white">{p.name}</div>
                    <div className="text-[11px] text-[#6B7280] mt-0.5 font-mono">
                      {p.critical_raid > 0 && <span className="text-[#FF007A] mr-3">▸ {p.critical_raid} critical RAID</span>}
                      {p.overdue_tasks > 0 && <span className="text-[#F59E0B] mr-3">▸ {p.overdue_tasks} overdue tasks</span>}
                      <span>{p.completion_pct}% complete</span>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#374151]" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Portfolio groups */}
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#6B7280] mb-3">Portfolios</div>
          {groups.length === 0 ? (
            <div className="border border-dashed border-[#1F2937] p-10 text-center text-sm text-[#6B7280]" data-testid="empty-portfolios">
              No portfolios yet. Group related projects (e.g. "Q1 EMEA Programme") to track them together.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1F2937] border border-[#1F2937]">
              {groups.map((g) => {
                const projs = overview.projects.filter((p) => (g.project_ids || []).includes(p.project_id));
                const avg = projs.length ? Math.round(projs.reduce((s, p) => s + p.completion_pct, 0) / projs.length) : 0;
                return (
                  <div key={g.group_id} className="bg-[#111827] p-5 group" data-testid={`portfolio-card-${g.group_id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-1 h-6" style={{ backgroundColor: g.color || "#00F5D4" }} />
                        <div className="font-display font-bold tracking-tight text-white">{g.name}</div>
                      </div>
                      <button onClick={() => deleteGroup(g.group_id)} className="text-[#374151] hover:text-[#FF007A] opacity-0 group-hover:opacity-100 transition" data-testid={`delete-portfolio-${g.group_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {g.description && <div className="text-xs text-[#9CA3AF] mt-2">{g.description}</div>}
                    <div className="mt-4 flex items-end justify-between">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">Projects</div>
                        <div className="font-display font-black text-2xl tracking-tighter text-white">{projs.length}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] text-right">Avg complete</div>
                        <div className="font-display font-black text-2xl tracking-tighter text-[#00F5D4]">{avg}%</div>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1">
                      {projs.slice(0, 5).map((p) => (
                        <span key={p.project_id} className="text-[10px] bg-[#1F2937] border border-[#374151] px-1.5 py-0.5 flex items-center gap-1 text-[#9CA3AF]">
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${HEALTH_DOT[p.health]}`} />
                          {p.name}
                        </span>
                      ))}
                      {projs.length > 5 && <span className="text-[10px] text-[#6B7280] px-1.5 py-0.5">+{projs.length - 5} more</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Full table — All projects rollup */}
        <div className="border border-[#1F2937]">
          <div className="border-b border-[#1F2937] px-6 py-3 font-display font-bold tracking-tight text-sm bg-[#111827] text-white">All projects rollup</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="portfolio-projects-table">
              <thead className="bg-[#030712] text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">
                <tr>
                  <th className="text-left px-4 py-2">Project</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-right px-4 py-2">Complete</th>
                  <th className="text-right px-4 py-2">Tasks</th>
                  <th className="text-right px-4 py-2">RAID</th>
                  <th className="text-right px-4 py-2">Gates</th>
                  <th className="text-right px-4 py-2">Billed</th>
                  <th className="text-right px-4 py-2">Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {overview.projects.map((p) => (
                  <tr key={p.project_id} className="border-t border-[#1F2937] hover:bg-[#111827]" data-testid={`portfolio-row-${p.project_id}`}>
                    <td className="px-4 py-2">
                      <Link to={`/workspace/${p.project_id}`} className="flex items-center gap-2 hover:underline text-white">
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${HEALTH_DOT[p.health]}`} />
                        <span className="font-medium">{p.name}</span>
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-xs text-[#9CA3AF] capitalize">{p.status.replace("_", " ")}</td>
                    <td className="px-4 py-2 text-right font-mono text-[#9CA3AF]">{p.completion_pct}%</td>
                    <td className="px-4 py-2 text-right font-mono text-[#9CA3AF]">{p.tasks_done}/{p.tasks_total}</td>
                    <td className="px-4 py-2 text-right font-mono">
                      <span className={p.critical_raid > 0 ? "text-[#FF007A] font-semibold" : "text-[#9CA3AF]"}>{p.open_raid}</span>
                      {p.critical_raid > 0 && <span className="text-[10px] text-[#FF007A] ml-1">({p.critical_raid} crit)</span>}
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-[#9CA3AF]">{p.gates_approved}/{p.gates_total}</td>
                    <td className="px-4 py-2 text-right font-mono text-[#00F5D4]">£{p.billed_total.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right font-mono">
                      <span className={p.outstanding_total > 0 ? "text-[#F59E0B]" : "text-[#374151]"}>
                        £{p.outstanding_total.toLocaleString()}
                      </span>
                    </td>
                  </tr>
                ))}
                {overview.projects.length === 0 && (
                  <tr><td colSpan="8" className="px-4 py-6 text-center text-[#6B7280] text-xs">No projects yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DarkPage>
  );
};

export default Portfolio;

const STATUS_BADGE = {
  on_track: { label: "On track", className: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  at_risk:  { label: "At risk",  className: "bg-amber-100 text-amber-700 border-amber-200" },
  critical: { label: "Critical", className: "bg-rose-100 text-rose-700 border-rose-200" },
};

const PulseHealthPanel = ({ pulseDash }) => {
  if (!pulseDash) return null;
  const { projects, critical_alerts_count, generated_at, total_projects } = pulseDash;
  const hasData = projects.length > 0;
  return (
    <div className="border border-[#1F2937] bg-[#111827]" data-testid="pulse-health-panel">
      <div className="px-5 py-3 border-b border-[#1F2937] flex flex-wrap items-center gap-x-3 gap-y-1">
        <Activity className="w-4 h-4 text-[#00F5D4]" />
        <h3 className="font-display font-bold tracking-tight text-sm text-white">Daily Pulse — Portfolio Health</h3>
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">
          {total_projects} project{total_projects === 1 ? "" : "s"}
        </span>
        {critical_alerts_count > 0 && (
          <span className="ml-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] bg-[#FF007A]/10 text-[#FF007A] border border-[#FF007A]/30 px-2 py-0.5" data-testid="pulse-critical-count">
            <AlertCircle className="w-3 h-3" />
            {critical_alerts_count} critical
          </span>
        )}
        <span className="ml-auto text-[10px] font-mono text-[#4B5563]">as of {generated_at}</span>
      </div>

      {!hasData ? (
        <div className="px-5 py-10 text-center text-sm text-[#6B7280]" data-testid="pulse-health-empty">
          No active projects. Create one to start tracking variance.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]" data-testid="pulse-health-table">
            <thead className="bg-[#030712] text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">
              <tr>
                <th className="text-left px-4 py-2">Project</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2">Hours burned</th>
                <th className="text-right px-4 py-2">Variance</th>
                <th className="text-left px-4 py-2">Timeline</th>
                <th className="text-left px-4 py-2">Latest blocker</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => {
                const badge = STATUS_BADGE[p.status] || STATUS_BADGE.on_track;
                const negative = p.variance_hours < 0;
                const burnPct = p.allocated_hours > 0 ? Math.min(100, (p.hours_burned / p.allocated_hours) * 100) : 0;
                return (
                  <tr key={p.project_id} className="border-t border-[#1F2937] hover:bg-[#0a0f1a]" data-testid={`pulse-health-row-${p.project_id}`}>
                    <td className="px-4 py-3">
                      <Link to={`/workspace/${p.project_id}`} className="font-semibold text-white hover:text-[#00F5D4] transition-colors" data-testid={`pulse-health-link-${p.project_id}`}>
                        {p.project_name}
                      </Link>
                      {p.total_budget > 0 && (
                        <div className="text-[10px] font-mono text-[#6B7280] mt-0.5">£{p.total_budget.toLocaleString()} contract</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 border ${badge.className}`} data-testid={`pulse-health-status-${p.project_id}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="font-mono text-sm text-white">{p.hours_burned}h</div>
                      <div className="text-[10px] text-[#6B7280]">of {p.allocated_hours}h</div>
                      {p.allocated_hours > 0 && (
                        <div className="h-1 bg-[#1F2937] mt-1 w-24 ml-auto">
                          <div className={`h-full ${p.status === "critical" ? "bg-[#FF007A]" : p.status === "at_risk" ? "bg-[#F59E0B]" : "bg-[#00F5D4]"}`} style={{ width: `${burnPct}%` }} />
                        </div>
                      )}
                    </td>
                    <td className={`px-4 py-3 text-right font-mono text-sm font-semibold ${negative ? "text-[#FF007A]" : "text-[#00F5D4]"}`}>
                      {negative ? "" : "+"}{p.variance_hours}h
                      <div className="text-[10px] font-normal">{negative ? "" : "+"}{p.variance_pct}%</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#9CA3AF] font-mono">day {p.days_elapsed} / {p.total_days}</td>
                    <td className="px-4 py-3 text-xs text-[#9CA3AF] max-w-[260px]">
                      {p.latest_blocker ? (
                        <span className="line-clamp-2" title={p.latest_blocker}>{p.latest_blocker}</span>
                      ) : (
                        <span className="text-[#374151]">—</span>
                      )}
                    </td>
                    <td className="px-2 py-3">
                      <Link to={`/workspace/${p.project_id}`} className="text-[#374151] hover:text-[#00F5D4] transition-colors">
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
